import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { AxiosInstance } from "axios";
import Router from "next/router";
import { useAuth } from "../auth";
import axios from "axios";
import { ToastProvider, useToast } from "../components/toast/ToastContainer";
import { getGrantedScopes, hasAppAccess } from "../auth/scopes";
import { shouldRefreshForRole } from "../auth/roleBootstrap";

interface AxiosContextProps {
  ax: AxiosInstance;
  axReady: boolean;
  /** Scopes Auth0 actually granted in the access token (decoded client-side). */
  grantedScopes: string[];
}

const AxiosContext = createContext<AxiosContextProps>(null);

export const AxiosProvider = ({ children }) => {
  const { getAccessToken, login } = useAuth();
  const [token, setToken] = useState(null);
  const [axReady, setAxReady] = useState(false);
  const [roleChecked, setRoleChecked] = useState(false);
  const { addToast } = useToast();

  const grantedScopes = useMemo(
    () => (token ? getGrantedScopes(token) : []),
    [token],
  );

  // Memoize the Axios instance so it doesn't get recreated on every render
  const axiosInstance = useMemo(() => {
    if (token) {
      return axios.create({
        baseURL: process.env.NEXT_PUBLIC_API_URL,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    }
    return null;
  }, [token]);

  // One interactive login at most: a burst of concurrent requests failing
  // token renewal must not stack redirect navigations.
  const loginPending = React.useRef(false);

  const bounceToLogin = useCallback(() => {
    if (loginPending.current) return;
    loginPending.current = true;
    // Router.asPath is basePath-less; normalizeReturnTo handles both shapes.
    login({ returnTo: Router.asPath }).catch((err) => {
      loginPending.current = false;
      console.error("Interactive login failed", err);
    });
  }, [login]);

  // Fetch the token per request: the SDK returns its cached token while valid
  // and renews via the rotating refresh token when expired. The instance used
  // to pin the mount-time token, so any tab open longer than the 1h token TTL
  // had every call 401 with no recovery path.
  const requestInterceptor = useCallback(
    async (config) => {
      try {
        const fresh = await getAccessToken();
        config.headers.Authorization = `Bearer ${fresh}`;
        // Keep grantedScopes (UI gating) in sync with the live token.
        setToken((prev) => (prev === fresh ? prev : fresh));
        return config;
      } catch (e) {
        // Renewal impossible (refresh token expired/revoked) — the session is
        // over. Re-login back to the current page instead of failing silently.
        bounceToLogin();
        throw e;
      }
    },
    [getAccessToken, bounceToLogin],
  );

  const onRequestFail = useCallback((error) => {
    return error;
  }, []);

  const responseInterceptor = useCallback((response) => {
    return response;
  }, []);

  const onResponseFail = useCallback(
    (error) => {
      const status = error?.response?.status;
      const method = (error?.config?.method ?? "get").toLowerCase();
      // Only surface 403s for user-initiated mutations. A GET 403 is almost
      // always a scope-gated component fetching data its tier can't read — a
      // render concern, not an action failure — so toasting it spams the user
      // (e.g. a doctor opening an order detail). The API stays the boundary;
      // the caller still gets the rejected promise to handle locally.
      if (status === 403 && method !== "get") {
        addToast("No estas autorizado para realizar esta accion.", "danger");
      }
      return Promise.reject(error);
    },
    [addToast],
  );

  useEffect(() => {
    const fetchToken = async () => {
      try {
        setToken(await getAccessToken());
      } catch (e) {
        // Not silently authenticable — bounce through interactive login,
        // returning to the current page (not home) afterwards.
        bounceToLogin();
      }
    };
    fetchToken();
  }, [getAccessToken, bounceToLogin]);

  useEffect(() => {
    if (axiosInstance) {
      axiosInstance.interceptors.request.use(requestInterceptor, onRequestFail);
      axiosInstance.interceptors.response.use(
        responseInterceptor,
        onResponseFail,
      );
      setAxReady(true);
    }
  }, [
    axiosInstance,
    requestInterceptor,
    onRequestFail,
    responseInterceptor,
    onResponseFail,
  ]);

  // First-login role bootstrap: GET /me triggers the backend identity binding
  // (which auto-assigns the doctor role). If our token still has no app scopes
  // but /me reports a privileged tier, the role was just granted — silently
  // refresh the token once to pick up its scopes. Capped via roleChecked.
  useEffect(() => {
    if (!axiosInstance || roleChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await axiosInstance.get("/me/");
        const tier = me?.data?.tier;
        if (
          !cancelled &&
          shouldRefreshForRole(hasAppAccess(grantedScopes), tier, false)
        ) {
          const fresh = await getAccessToken({ forceRefresh: true });
          if (!cancelled) setToken(fresh);
        }
      } catch (e) {
        // /me failed (transient/offline) — keep current scopes; never block.
      } finally {
        if (!cancelled) setRoleChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [axiosInstance, roleChecked, grantedScopes, getAccessToken]);

  if (!axiosInstance) {
    return (
      <div className="d-flex align-items-center">
        <strong role="status">Autenticando...</strong>
      </div>
    );
  }

  return (
    <AxiosContext.Provider value={{ ax: axiosInstance, axReady, grantedScopes }}>
      {children}
    </AxiosContext.Provider>
  );
};

export const useAxios = () => {
  return useContext(AxiosContext);
};
